import { useEffect, type ReactNode } from 'react';
import { ConfirmationProfessionalContainer } from './ConfirmationProfessionalContainer';
import { ConfirmationParticipantsContainer } from './ConfirmationParticipantsContainer';
import { useModal } from '@/hooks/useModal';
import { ConfirmationLocationContainer } from './ConfirmationLocationContainer';
import { CalendarDays, Clock3, Video, type LucideIcon } from 'lucide-react';
import { formatProfessionalFullName } from '@/utils';
import { cn } from '@/lib/utils';

interface ConfirmationModalContentProps {
	professionalData: Professionals.Professional;
	scheduleData: {
		date: string;
		time: string;
	};
	addressData: Clinics.Address | null;
	selectedModality: Professionals.ServiceModality;
	/** IDs dos participantes (admin: múltiplos pacientes). Opcional no fluxo usuário. */
	participantIds?: string[];
}

interface DetailFieldProps {
	icon: LucideIcon;
	label: string;
	value: string;
}

const DetailCard = ({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) => (
	<div
		className={cn(
			'rounded-2xl border border-gray-100 bg-gray-50 p-5',
			className,
		)}
	>
		{children}
	</div>
);

const DetailField = ({ icon: Icon, label, value }: DetailFieldProps) => (
	<>
		<div className="mb-3 flex items-center gap-2 text-gray-400">
			<Icon size={16} />
			<span className="text-[10px] font-bold uppercase tracking-wider">
				{label}
			</span>
		</div>
		<p className="text-sm font-bold text-gray-800">{value}</p>
	</>
);

const ConfirmationScheduleSummary = ({
	date,
	time,
	typeLabel,
}: {
	date: string;
	time: string;
	typeLabel: string;
}) => (
	<div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
		<DetailCard className="p-4 md:hidden">
			<div className="grid grid-cols-2 divide-x divide-gray-200">
				<div className="pr-4">
					<DetailField
						icon={CalendarDays}
						label="Data"
						value={date}
					/>
				</div>
				<div className="pl-4">
					<DetailField icon={Clock3} label="Horário" value={time} />
				</div>
			</div>
		</DetailCard>

		<DetailCard className="hidden md:block">
			<DetailField icon={CalendarDays} label="Data" value={date} />
		</DetailCard>

		<DetailCard className="hidden md:block">
			<DetailField icon={Clock3} label="Horário" value={time} />
		</DetailCard>

		<DetailCard className="hidden md:block">
			<DetailField icon={Video} label="Tipo" value={typeLabel} />
		</DetailCard>
	</div>
);

export const ConfirmationModalContent = ({
	addressData,
	professionalData,
	scheduleData,
	selectedModality,
	participantIds,
}: ConfirmationModalContentProps) => {
	const { setConfirmText } = useModal();

	useEffect(() => {
		setConfirmText('Próxima etapa');
	}, [setConfirmText]);

	const typeLabel =
		selectedModality === 'FULL_PRESENCIAL'
			? 'Atendimento presencial'
			: 'Teleconsulta (online)';
	const professionalFullName = formatProfessionalFullName(professionalData);

	return (
		<section className="flex w-full justify-center">
			<div className="w-full max-w-2xl space-y-4 rounded-[2.5rem] p-6 md:space-y-6">
				<ConfirmationProfessionalContainer
					imgUrl={professionalData.profile_photo_url || ''}
					name={professionalFullName}
					code={professionalData.crm_or_crp}
					description={professionalData.brief_description || ''}
				/>

				{participantIds && participantIds.length > 0 && (
					<>
						<div className="h-px w-full bg-zinc-200" />
						<ConfirmationParticipantsContainer
							participantIds={participantIds}
						/>
					</>
				)}

				<div className="h-px w-full bg-zinc-200" />

				<ConfirmationScheduleSummary
					date={scheduleData.date}
					time={scheduleData.time}
					typeLabel={typeLabel}
				/>

				<div className="hidden h-px w-full bg-zinc-200 md:block" />

				<ConfirmationLocationContainer
					serviceModality={selectedModality}
					clinicAddress={addressData}
					typeLabel={typeLabel}
				/>
			</div>
		</section>
	);
};
